import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import PrimaryButton from '../../../../../components/Buttons/PrimaryButton';
import { DialogContext } from '../../../../../store/context/DialogContext';
import { useTranslation } from '../../../../../store/context/TranslationContext';
import { doGET } from '../../../../../util/HttpUtil';
import { DOC_TYPE_FORM_TYPES } from '../../../../../util/docTypeRuleDefaults';
import useDocTypeRules from '../../../../../hooks/useDocTypeRules';
import { useDocTypes } from '../../../../../util/docTypeRuleMeta';
import ButtonGroupNew from '../../BusinessLiteForms/components/ButtonGroupNew';
import ProofOfDocuments from '../../BusinessLiteForms/components/ProofOfDocument';
import { formatIndividualName } from '../../../../../util/docTypeRuleEvaluator';
import { buildAutomationChildTestId } from '../../../../../components/InputField/testId';

const PROOF_OF_ADDRESS_TEST_ID = "individual-plus-proof-of-address";

const ProofOfAddressDocument = ({
    hideButtons,
    isAskForInfo,
    remark,
    recordId,
    asLabel,
    setStep,
    handleUpdate,
    setHasError,
    setErrorMessage = () => { },
    data,
    backStep = 2,
    nextStep,
    // Broker mode (D2): the Document step is the broker's LAST step — its "Next" submits
    // (markFormSubmit+markDocSubmit) instead of advancing to PAY_NOW, then lands on
    // brokerSubmitNextStep (ThankYou). KYC is completed later by the client via the link.
    isBrokerSubmitStep = false,
    brokerSubmitNextStep,
}) => {

    const [loading, setLoading] = useState(false);
    const [iframeLoading, setIframeLoading] = useState(false);
    const [iframeVisible, setIframeVisible] = useState(false);
    const [iframeUrl] = useState('');

    const [docStatuses, setDocStatuses] = useState({});

    const {
        docTypes: ruleDocTypes,
        loading: docRuleLoading,
        error: docRuleError,
    } = useDocTypeRules({
        formType: DOC_TYPE_FORM_TYPES.INDIVIDUAL_PLUS,
        applicationData: data,
    });
    const { getMeta, loading: docTypesLoading, error: docTypesError } = useDocTypes();

    const handleValidationStatusChange = (docId, status) => {
        if (!docId) return;
        setDocStatuses(prev => ({
            ...prev,
            [docId]: status
        }));
    };


    const { showMessage, showError } = useContext(DialogContext);
    const { t } = useTranslation();

    const checkIsDocumentUploaded = async ({ type }) => {
        try {
            const response = await doGET(`/api/application-doc/grid?rows=-1&form_id=${recordId}${data?.ubo_id ? `&ubo_id=${data?.ubo_id}` : ""}&type=${type}`);
            if (response?.status === 200 && response?.data?.rows[0]?.attachment?.urls?.length) {
                return true;
            }
        } catch (error) {
            showError(error);
        }
        return false;
    };

    const effectiveDocTypes = useMemo(() => (ruleDocTypes?.length ? ruleDocTypes : ["ProofOfAddress"]), [ruleDocTypes]);

    useEffect(() => {
        if (docRuleError) {
            showError(docRuleError?.message || "Failed to load document rules.");
        }
    }, [docRuleError, showError]);

    useEffect(() => {
        if (docTypesError) {
            showError(docTypesError?.message || "Failed to load document types.");
        }
    }, [docTypesError, showError]);

    const stepNumber = backStep + 1;
    const resolvedNextStep = nextStep ?? (backStep + 2);

    const handleSubmit = async () => {

        const errors = [];
        setLoading(true);

        for (const docType of effectiveDocTypes) {
            const isDocumentUploaded = await checkIsDocumentUploaded({ type: docType });
            if (!isDocumentUploaded) {
                const meta = getMeta(docType, { displayName: formatIndividualName(data), });
                const message = meta.errorMessage || "Please upload the required document.";
                errors.push(message);
            }
        }

        if (errors.length > 0) {
            showError(errors[0]);
        }
        if (errors.length > 0) {
            setHasError(true);
            setErrorMessage(errors.join(' '));
            setLoading(false);
            return;
        }

        setHasError(false);
        if (isBrokerSubmitStep) {
            // Broker's final step: submit form + documents (payment waived server-side),
            // then go to the confirmation step. The broker never reaches KYC.
            await handleUpdate({ isfetchDetail: true, isLastStep: true, skipUpdate: true, nextStep: brokerSubmitNextStep });
        } else {
            await handleUpdate({ nextStep: resolvedNextStep, skipUpdate: true });
        }
        setLoading(false);
    };

    const handleIframeMessage = (event) => {
        if (event.data === '{"event":"DOCUMENT-CAPTURED"}') {
            handleSubmit({ source: "YUG" });
        }
    };

    const handleUploadOfflineClick = () => {
        setIframeVisible(false);
    };

    const handleDownloadOnlineClick = () => {
        setIframeVisible(true);
    };

    const handleIframeLoadStart = () => {
        setIframeLoading(true);
    };

    const handleIframeLoadEnd = () => {
        setIframeLoading(false);
    };

    return (
        <div className='' data-testid={PROOF_OF_ADDRESS_TEST_ID} data-record-id={recordId}>
            {iframeVisible ? (
                <div data-testid={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, "online-capture")}>
                    {(loading || !iframeUrl) ? (
                        <div style={{ width: '100%', height: 'calc(100vh - 340px)' }} className="d-flex justify-content-center align-items-center w-100 flex-1 fv_ondato_rule" data-testid={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, "online-capture-loading")}>
                            <FaSpinner size={14} className="spinner" />
                        </div>
                    ) : (null)}
                    <iframe
                        data-testid={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, "online-capture-iframe")}
                        src={iframeUrl}
                        style={{ width: '100%', height: iframeLoading ? 0 : 'calc(100vh - 320px)' }}
                        onLoadStart={handleIframeLoadStart}
                        onLoad={handleIframeLoadEnd}
                    />
                </div>
            ) : (
                <div data-testid={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, "offline-upload")}>
                    {(docRuleLoading || docTypesLoading || loading) && (
                        <div style={{ width: '100%', height: 'calc(100vh - 340px)' }} className="d-flex justify-content-center align-items-center w-100 flex-1 fv_ondato_rule" data-testid={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, "loading")}>
                            <FaSpinner size={14} className="spinner" />
                        </div>
                    )}
                    <div data-testid={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, "document-list")}>
                    {!docRuleLoading && !docTypesLoading && !loading && effectiveDocTypes.map((docType) => {
                        const meta = getMeta(docType, { displayName: formatIndividualName(data), });
                        return (
                            <ProofOfDocuments
                                key={docType}
                                dataTestId={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, `document-${docType}`)}
                                isAskForInfo={isAskForInfo}
                                remark={remark}
                                recordId={recordId}
                                asLabel={asLabel}
                                validateByAi={meta.validateByAi}
                                onValidationStatusChange={handleValidationStatusChange}
                                label={t(meta.label)}
                                field={meta.field}
                                showCategory={meta.showCategory}
                                type={meta.type}
                                ubo_id={data?.ubo_id}
                                single={meta.single}
                                supportedFiles={meta.supportedFiles}
                            />
                        );
                    })}
                    </div>
                </div>
            )}

            {!hideButtons && <div className='mt-4' data-testid={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, "footer")}>
                <ButtonGroupNew
                    testId={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, "navigation")}
                    primaryActionTestIdSuffix="continue"
                    onNextClick={handleSubmit}
                    onBackClick={() => { setStep(backStep) }}
                    step={stepNumber}
                    showPageNum={false}
                    pageNum={stepNumber}
                    isDisabled={asLabel || docRuleLoading || docTypesLoading}
                    asLabel={false}
                    loading={loading || docRuleLoading || docTypesLoading}
                />
            </div>}


            {!hideButtons && (
                <div className='d-flex w-100 justify-content-between '>
                    <div className='d-flex'>
                        {iframeVisible && (
                            <PrimaryButton
                                data-testid={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, "download-online")}
                                onClick={handleDownloadOnlineClick}
                                className="me-2"
                            >
                                Download Online
                            </PrimaryButton>
                        )}
                        {iframeVisible && (
                            <PrimaryButton
                                data-testid={buildAutomationChildTestId(PROOF_OF_ADDRESS_TEST_ID, "upload-offline")}
                                onClick={handleUploadOfflineClick}
                                className="me-2"
                            >
                                Upload Offline
                            </PrimaryButton>
                        )}


                    </div>
                </div>
            )}
        </div>
    );
};

export const InputLabel = ({ label, required, labelStyle }) => (
    <div style={{ minWidth: "120px", fontSize: 16, margin: "5px 0px 0px 5px", fontWeight: "500", ...labelStyle }}>
        {label}
        {required && <font color="red">*</font>}
    </div>
);

export default ProofOfAddressDocument;
